import React, { useState } from 'react';
import { Calculator, Copy, Trash2, Wallet } from 'lucide-react';

const SalaryCalculatorTool = () => {
  const [annualSalary, setAnnualSalary] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const annual = parseFloat(annualSalary);

    if (isNaN(annual) || annual < 0) {
      setResult(null);
      return;
    }

    // Calculate salary breakdowns
    const monthly = annual / 12;
    const biweekly = annual / 26;
    const weekly = annual / 52;
    const daily = annual / 260; // 5 days × 52 weeks
    const hourly = annual / 2080; // 40 hours × 52 weeks

    setResult({
      annual,
      monthly,
      biweekly,
      weekly,
      daily,
      hourly
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setAnnualSalary('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Annual Salary ($)
        </label>
        <input
          type="number"
          step="0.01"
          value={annualSalary}
          onChange={(e) => setAnnualSalary(e.target.value)}
          placeholder="e.g., 75000"
          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
          data-testid="annual-salary-input"
        />
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-salary-btn"
      >
        <Calculator size={18} />
        Calculate Salary
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="salary-results">
          {/* Main Results Grid */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Monthly</span>
                <button
                  onClick={() => copyResult(result.monthly, 'monthly')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'monthly' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-monthly"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-green-400" data-testid="monthly-result">
                {formatCurrency(result.monthly)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Weekly</span>
                <button
                  onClick={() => copyResult(result.weekly, 'weekly')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'weekly' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-weekly"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-blue-400" data-testid="weekly-result">
                {formatCurrency(result.weekly)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Daily</span>
                <button
                  onClick={() => copyResult(result.daily, 'daily')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'daily' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-daily"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-yellow-400" data-testid="daily-result">
                {formatCurrency(result.daily)}
              </div>
            </div>
          </div>

          {/* Additional Breakdowns */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Bi-weekly</span>
                <button
                  onClick={() => copyResult(result.biweekly, 'biweekly')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'biweekly' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-xl font-bold text-purple-400">
                {formatCurrency(result.biweekly)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Hourly (40hr week)</span>
                <button
                  onClick={() => copyResult(result.hourly, 'hourly')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'hourly' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-xl font-bold text-cyan-400">
                {formatCurrency(result.hourly)}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Wallet size={16} className="text-green-400" />
              <h4 className="text-sm font-medium text-gray-300">Salary Breakdown</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Annual</span>
                <span className="text-white font-medium">{formatCurrency(result.annual)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Monthly (÷12)</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.monthly)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Bi-weekly (÷26)</span>
                <span className="text-purple-400 font-medium">{formatCurrency(result.biweekly)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Weekly (÷52)</span>
                <span className="text-blue-400 font-medium">{formatCurrency(result.weekly)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Daily (÷260 work days)</span>
                <span className="text-yellow-400 font-medium">{formatCurrency(result.daily)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Hourly (÷2080 hours)</span>
                <span className="text-cyan-400 font-medium">{formatCurrency(result.hourly)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Calculation Assumptions</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• 12 months per year</div>
          <div>• 52 weeks per year (26 bi-weekly periods)</div>
          <div>• 260 working days (5 days × 52 weeks)</div>
          <div>• 2,080 working hours (40 hours × 52 weeks)</div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Note</h3>
        <div className="text-sm text-gray-400">
          This calculator shows gross salary before taxes and deductions. Actual take-home pay will be lower after federal, state, and local taxes, plus benefits deductions.
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-salary-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default SalaryCalculatorTool;
